import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Rocket, ExternalLink, BookOpen } from 'lucide-react';
import { typography, components } from '../utils/designTokens';
import OnboardingService from '../services/OnboardingService';
import { DOCS_URL } from '../config';

const OnboardingModal = ({ isOpen, onClose, stytch }) => {
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyAlreadyExists, setApiKeyAlreadyExists] = useState(false);
  const [loadingApiKey, setLoadingApiKey] = useState(true);
  const [creatingKey, setCreatingKey] = useState(false);
  const [keyCreated, setKeyCreated] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  useEffect(() => {
    if (!isOpen || !stytch) return;

    const checkForExistingKey = async () => {
      try {
        const jwt = stytch?.session.getTokens()?.session_jwt;
        if (!jwt) return;

        // Check if default key already exists
        const existingKey = await OnboardingService.checkForDefaultKey(jwt);

        if (existingKey.exists) {
          // Key already exists, user has already copied it
          setApiKeyAlreadyExists(true);
          setApiKey('already-copied-key');
        }
      } catch (error) {
        console.error('Failed to check for existing key:', error);
      } finally {
        setLoadingApiKey(false);
      }
    };

    checkForExistingKey();
  }, [isOpen, stytch]);

  const handleCreateKey = async () => {
    if (!stytch) return;

    try {
      setCreatingKey(true);
      const jwt = stytch?.session.getTokens()?.session_jwt;
      if (!jwt) return;

      const newKey = await OnboardingService.createDefaultKey(jwt);
      setApiKey(newKey.fullKey); // client_id:client_secret
      setKeyCreated(true);
      setApiKeyAlreadyExists(false);
    } catch (error) {
      console.error('Failed to create API key:', error);
      alert('Failed to create API key. Please try again.');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleCopyKey = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (error) {
      console.error('Failed to copy API key:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background-secondary border-b border-accent-lavender/20 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Rocket className="w-6 h-6 text-accent-amber" />
            <h2 className={`${typography.size.xl} ${typography.weight.bold} text-text-primary`}>
              Welcome to Lumoz!
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          <p className={`${typography.size.base} text-text-secondary`}>
            Get started by sending your first trace in 2 simple steps:
          </p>

          {/* Step 1: Get API Key */}
          <div className={`${components.card.bg} ${components.card.padding} ${components.card.rounded} ${components.card.border}`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-amber/20 flex items-center justify-center">
                <span className={`${typography.weight.bold} text-accent-amber`}>1</span>
              </div>
              <div className="flex-1">
                <h3 className={`${typography.size.base} ${typography.weight.semibold} text-text-primary mb-2`}>
                  Create Your API Key
                </h3>
                {loadingApiKey ? (
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <div className="w-4 h-4 border-2 border-accent-primary-base border-t-transparent rounded-full animate-spin"></div>
                    Loading...
                  </div>
                ) : apiKeyAlreadyExists ? (
                  <>
                    <p className={`${typography.size.sm} text-text-secondary mb-2`}>
                      Your default API key has already been created. Use the key you copied earlier in the code below.
                    </p>
                    <div className="bg-background-tertiary border border-accent-lavender/20 rounded px-3 py-2">
                      <code className="text-xs text-text-muted font-mono">already-copied-key</code>
                    </div>
                  </>
                ) : keyCreated && apiKey ? (
                  <>
                    <p className={`${typography.size.sm} text-text-secondary mb-3`}>
                      Your API key has been created! Copy it now - this is the only time you'll see the secret.
                    </p>
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2 mb-3">
                      <p className="text-amber-400 text-xs font-medium mb-1">⚠️ Save this key securely!</p>
                      <p className="text-text-secondary text-xs">You won't be able to see the secret again.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-background-tertiary border border-accent-lavender/20 rounded px-3 py-2">
                        <code className="text-xs text-text-primary font-mono break-all">{apiKey}</code>
                      </div>
                      <button
                        onClick={handleCopyKey}
                        className="flex items-center gap-1.5 px-3 py-2 bg-accent-primary-base text-text-inverse rounded text-sm font-medium hover:bg-accent-primary-vibrant transition-colors"
                      >
                        {copiedKey ? (
                          <>
                            <Check size={16} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={16} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className={`${typography.size.sm} text-text-secondary mb-3`}>
                      Create a default API key to authenticate your application with Lumoz.
                    </p>
                    <button
                      onClick={handleCreateKey}
                      disabled={creatingKey}
                      className="flex items-center gap-2 px-4 py-2 bg-accent-primary-base text-text-inverse rounded text-sm font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingKey ? (
                        <>
                          <div className="w-4 h-4 border-2 border-dark border-t-transparent rounded-full animate-spin"></div>
                          Creating...
                        </>
                      ) : (
                        'Create API Key'
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Step 2: Send First Trace */}
          <div className={`${components.card.bg} ${components.card.padding} ${components.card.rounded} ${components.card.border}`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-amber/20 flex items-center justify-center">
                <span className={`${typography.weight.bold} text-accent-amber`}>2</span>
              </div>
              <div className="flex-1">
                <h3 className={`${typography.size.base} ${typography.weight.semibold} text-text-primary mb-2`}>
                  Send Your First Trace
                </h3>
                <p className={`${typography.size.sm} text-text-secondary mb-3`}>
                  Use your API key to instrument your application. Visit the examples repository for connectivity tests, framework guides, and ready-to-run examples.
                </p>

                <a
                  href="https://github.com/lumozai/examples"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent-primary-base text-text-inverse rounded text-sm font-medium hover:bg-accent-primary-vibrant transition-colors"
                >
                  <ExternalLink size={16} />
                  View Examples Repository
                </a>

                <div className="flex gap-3 mt-3">
                  <a
                    href={DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-accent-amber hover:text-accent-gold transition-colors"
                    onClick={onClose}
                  >
                    <BookOpen size={16} />
                    View Full Documentation
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-secondary border-t border-accent-lavender/20 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-background-tertiary transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
